import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import GridOnRoundedIcon from '@mui/icons-material/GridOnRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import {
  Box,
  Radio,
  Typography,
} from '@mui/material';
import styled from 'styled-components';

import { updateCoordinateSystem } from 'store/reducers/inputReducer';
import { selectCoordinateSystem } from 'store/reducers/selectors';
import { selectRoadNetworkAccess } from 'store/functions/entitlements';

const coordinateSystemConfigs = require('store/data/coordinate_system_configs.json');

const Container = styled.div`
  border: ${props => props.$compact ? 'none' : '1.5px solid #e9ecef'};
  border-radius: ${props => props.$compact ? '0' : '12px'};
  overflow: hidden;
  background: #fff;
  margin-bottom: ${props => props.$compact ? '0' : '4px'};
`;

const Header = styled.div`
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f0f0f0;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.$compact ? '10px' : '14px'};
  padding: ${props => props.$compact ? '10px 14px' : '12px 20px'};
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid ${props => props.$last ? 'transparent' : '#f5f5f5'};
  background: ${props => props.$active ? 'rgba(0, 146, 139, 0.04)' : 'transparent'};

  &:hover {
    background: ${props => props.$active ? 'rgba(0, 146, 139, 0.06)' : '#fafafa'};
  }
`;

const IconBox = styled.div`
  width: ${props => props.$compact ? '28px' : '36px'};
  height: ${props => props.$compact ? '28px' : '36px'};
  border-radius: 8px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  background: ${props => props.$active ? '#017C76' : '#f4f5f7'};
  color: ${props => props.$active ? '#fff' : '#525f7f'};
  font-size: 18px;
  transition: all 0.15s ease;
`;

/**
 * Choose whether the scenario's locations are geographic (latitude/longitude,
 * shown on a map) or cartesian (an arbitrary x-y grid, shown on a plot).
 *
 * This is a property of the *data*, not of the model, so it applies to any
 * optimization model. Used both as a wizard step and — in `compact` form —
 * inside the map toolbar popover.
 *
 * Labels and descriptions come from `coordinate_system_configs.json`, the same
 * file that drives the X/Y column relabeling, so the two can't drift apart.
 */
const CoordinateSystemChoice = ({ compact = false, showHeader = true }) => {
  const dispatch = useDispatch();
  const coordinateSystem = useSelector(selectCoordinateSystem);
  // Sent with the switch so the "what changed" notice names the method a
  // calculation would really use for this user (see costMethods).
  const roadNetworkAllowed = useSelector(selectRoadNetworkAccess);

  // Geographic first: it's the default and by far the common case.
  const options = [
    { value: 'geographic', icon: <PublicRoundedIcon fontSize="small" /> },
    { value: 'cartesian', icon: <GridOnRoundedIcon fontSize="small" /> },
  ].map((option) => ({
    ...option,
    label: coordinateSystemConfigs[option.value]?.label ?? option.value,
    description: coordinateSystemConfigs[option.value]?.description ?? '',
  }));

  return (
    <Container $compact={compact}>
      {showHeader && (
        <Header>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#32325d', mb: 0.25 }}>
            How are your locations positioned?
          </Typography>
          <Typography variant="body2" sx={{ color: '#525f7f', fontSize: '0.8rem' }}>
            This sets how locations are entered and displayed. You can change it later.
          </Typography>
        </Header>
      )}

      {options.map((option, idx) => {
        const active = coordinateSystem === option.value;
        return (
          <OptionRow
            key={option.value}
            $active={active}
            $compact={compact}
            $last={idx === options.length - 1}
            onClick={() => dispatch(updateCoordinateSystem({ coordinateSystem: option.value, roadNetworkAllowed }))}
          >
            <IconBox $active={active} $compact={compact}>
              {option.icon}
            </IconBox>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: active ? '#017C76' : '#32325d', lineHeight: 1.3 }}
              >
                {option.label}
              </Typography>
              {!compact && (
                <Typography variant="body2" sx={{ color: '#525f7f', fontSize: '0.78rem' }}>
                  {option.description}
                </Typography>
              )}
            </Box>

            <Radio
              checked={active}
              size="small"
              sx={{ color: '#ced4da', '&.Mui-checked': { color: '#017C76' }, p: 0 }}
            />
          </OptionRow>
        );
      })}
    </Container>
  );
};

CoordinateSystemChoice.propTypes = {
  /** Tighter spacing and no descriptions — for the map toolbar popover. */
  compact: PropTypes.bool,
  showHeader: PropTypes.bool,
};

export default CoordinateSystemChoice;
